export const LISTENING_PRELOAD_WINDOW = 3;
export const AUDIO_PRELOAD_TIMEOUT_MS = 60000;

export const isAudioFullyBuffered = (audio) => {
  if (!Number.isFinite(audio?.duration) || audio.duration <= 0) return false;
  const ranges = audio.buffered;
  return ranges?.length > 0 && ranges.start(0) === 0 &&
    ranges.length === 1 && ranges.end(0) >= audio.duration - 0.05;
};

export const isListeningQuestion = (question) =>
  question?.Category === "Listening";

export const getQuestionId = (question) =>
  question?.QuestionId !== undefined && question?.QuestionId !== null
    ? String(question.QuestionId)
    : "";

export const getListeningAudioUrl = (questionData) => {
  const firstAudio = questionData?.AudioObjects?.[0];
  return firstAudio?.url || firstAudio?.audio_url || questionData?.audio_url || "";
};

export const getListeningQuestionsFromStep = (questions = [], currentStep = 1) =>
  questions
    .slice(Math.max(0, currentStep - 1))
    .filter(isListeningQuestion);

export const getListeningPreloadBatches = (
  questions = [],
  preloadWindow = LISTENING_PRELOAD_WINDOW
) => {
  const listeningQuestions = questions.filter(isListeningQuestion);

  return {
    initialQuestions: listeningQuestions.slice(0, preloadWindow),
    backgroundQuestions: listeningQuestions.slice(preloadWindow),
  };
};

export const getPreloadReadyCount = (
  questions = [],
  audioStatusByQuestionId = {},
  preloadWindow = LISTENING_PRELOAD_WINDOW
) => {
  return questions
    .filter(isListeningQuestion)
    .slice(0, preloadWindow)
    .filter((question) => audioStatusByQuestionId[getQuestionId(question)] === "ready")
    .length;
};

export const isTerminalAudioStatus = (status) =>
  status === "ready" || status === "error" || status === "no-audio";

export const preloadAudioUrl = (
  audioUrl,
  {
    timeoutMs = AUDIO_PRELOAD_TIMEOUT_MS,
    AudioCtor = typeof Audio !== "undefined" ? Audio : null,
    signal,
  } = {}
) => {
  if (!audioUrl) {
    return Promise.resolve("no-audio");
  }

  if (!AudioCtor) {
    return Promise.resolve("ready");
  }

  return new Promise((resolve) => {
    const audio = new AudioCtor();
    let settled = false;
    let timeoutId;

    const cleanup = () => {
      audio.removeEventListener("canplaythrough", onReady);
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("loadeddata", onReady);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("progress", onReady);
      audio.removeEventListener("suspend", onReady);
      signal?.removeEventListener("abort", onAbort);
      clearTimeout(timeoutId);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };

    const finish = (status) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(status);
    };

    const onReady = () => {
      if (isAudioFullyBuffered(audio)) finish("ready");
    };
    const onError = () => finish("error");
    const onAbort = () => finish("cancelled");

    if (signal?.aborted) {
      onAbort();
      return;
    }

    audio.preload = "auto";
    audio.addEventListener("canplaythrough", onReady);
    audio.addEventListener("canplay", onReady);
    audio.addEventListener("loadeddata", onReady);
    audio.addEventListener("error", onError);
    audio.addEventListener("progress", onReady);
    audio.addEventListener("suspend", onReady);
    signal?.addEventListener("abort", onAbort);
    audio.src = audioUrl;
    timeoutId = setTimeout(() => finish("error"), timeoutMs);

    try {
      audio.load();
    } catch (error) {
      finish("error");
      return;
    }

  });
};
